// TODO: cleanup with research_assistant_gen_3 ff
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getProjectCohortVariableViewPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import { useGetConceptGroupsQuery } from '@verantos-dev/data-access-concept-groups';
import {
  AnalyticalVariableCard,
  AvContentCard,
  toConceptGroupOptions,
  useVariableDetail,
} from '@verantos-dev/feature-analytical-variables';
import { AvSlugClickProvider, type EpiArtifact } from '@verantos-dev/feature-epi-chat';
import { SubPageBreadcrumbHeader } from '@verantos-dev/shared-components';

import { useActiveChatContext } from '../context/ActiveChatContext';
import { useVariableViewBreadcrumbs } from '../hooks/useVariableViewBreadcrumbs';

// OR-6115 shape: an inline AV reference hands back a bare slug, an
// already-qualified knowledge id (``av.<slug>``), or a locked-library UUID.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Standalone AV view for the agent-first project page (entered via
 * ``/projects/:projectId/variable/:analyticalVariableId`` from the artifact
 * list, the chat bubble click handler, or the focus_scope navigator).
 *
 * The ``VariableDraft`` is the SINGLE rendering source — chat/RA events are
 * TRIGGERS (they invalidate + refetch the by-ref detail), never state. This
 * page resolves the AV via REST: the KNOWLEDGE store (``av.<slug>``-keyed
 * detail) or the library (``ANALYTICAL_VARIABLE`` UUID detail). The portable
 * :class:`AvContentCard` owns the rendering — header, tabs, tab bodies,
 * status chip — so this page is a thin shell around data acquisition
 * and route-specific chrome. Library DSL (``av_ir``) AVs continue to
 * render the legacy ``AnalyticalVariableCard`` read-only view since
 * the DSL surface predates the tabbed shell.
 */
export const ProjectVariableViewPage = () => {
  const theme = useTheme();
  const {
    analyticalVariableId = '',
    projectId = '',
    cohortId,
  } = useParams<{
    analyticalVariableId: string;
    projectId: string;
    cohortId?: string;
  }>();
  const navigate = useNavigate();
  const ctx = useActiveChatContext();

  // OR-6306: an inline AV-slug reference in this variable's own Description/
  // SQL tabs should route to the referenced variable's detail view — keeping
  // the cohort scope when this page itself is cohort-scoped — instead of
  // falling back to the legacy knowledge-browser dialog.
  const handleAvSlugClick = useCallback(
    (idOrSlug: string) => {
      if (!projectId) return;
      const knowledgeId =
        idOrSlug.startsWith('av.') || UUID_PATTERN.test(idOrSlug) ? idOrSlug : `av.${idOrSlug}`;
      navigate(
        cohortId
          ? getProjectCohortVariableViewPath(projectId, cohortId, knowledgeId)
          : getProjectVariablePath(projectId, knowledgeId)
      );
    },
    [navigate, projectId, cohortId]
  );

  // The URL id may be ``av.<slug>`` (canonical KNOWLEDGE id), a bare
  // slug (legacy chat-bubble click), or a UUID (cohort-builder library
  // row). Strip the prefix when present for the breadcrumb fallback and
  // the "Could not load…" copy.
  const matchSlug = analyticalVariableId.startsWith('av.')
    ? analyticalVariableId.slice('av.'.length)
    : analyticalVariableId;

  // OR-6290 (asset workflow): the VariableDraft is the SINGLE rendering
  // source — chat/RA events are TRIGGERS (they invalidate + refetch the
  // by-ref detail), never state. There is no live in-flight artifact to
  // render from here.
  const liveArtifact: EpiArtifact | null = null;

  // OR-6344 / OR-6345: the draft is committed synchronously
  // (definition-first) and resolvable via the ``by-ref`` detail endpoint,
  // which reads the live in-flight DynamoDB draft for the active chat
  // (``chatId``) — so there is no "pre-manifest" window to skeleton.
  const detailQuery = useVariableDetail(
    analyticalVariableId || null,
    projectId || null,
    // The chat/session id lets the detail endpoint resolve a still-drafting
    // AV from the live in-flight store on a hard refresh (no KNOWLEDGE row
    // exists yet). Null when no chat is open — Snowflake-only resolution.
    ctx?.chatId ?? null
  );

  const { data: conceptGroupsResponse, isLoading: isConceptGroupsLoading } =
    useGetConceptGroupsQuery({ urlParams: '' });

  // Breadcrumb-only header (OR-6123 F8). The AV cards (`AvContentCard` /
  // `AnalyticalVariableCard`) already render the variable name as their own
  // title, so the page renders the breadcrumb trail above them rather than a
  // second title. Breadcrumb label prefers the human-readable data element
  // (e.g. "First lung cancer diagnosis date"), falling back to the slug.
  const variableName = detailQuery.data?.data?.name ?? undefined;
  const variableDataElement = detailQuery.data?.data?.dataElement ?? undefined;
  const { breadcrumbs } = useVariableViewBreadcrumbs({
    variableName: variableDataElement ?? variableName ?? (matchSlug || undefined),
    isVariableLoading: detailQuery.isLoading,
  });
  const breadcrumbHeader = (
    <SubPageBreadcrumbHeader id="project-variable-view-header" breadcrumbs={breadcrumbs} />
  );

  // ---------- render branches ----------

  if (detailQuery.isLoading) {
    return (
      <Box data-testid="project-variable-view-page">
        {breadcrumbHeader}
        <Box
          sx={{ paddingX: 6, paddingBottom: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Skeleton variant="text" width={240} height={32} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={200} />
        </Box>
      </Box>
    );
  }

  // Library DSL AV — keep the legacy ``AnalyticalVariableCard``.
  // ``jsonValue`` is what the card renders; library rows without it
  // are SQL-first (``av_denovo``) and fall through to the AvContentCard
  // surface below (same shell knowledge-backed rows use).
  if (detailQuery.data?.data?.source === 'library') {
    const detail = detailQuery.data.data;
    if (detail.jsonValue) {
      if (isConceptGroupsLoading) {
        return (
          <Box data-testid="project-variable-view-page">
            {breadcrumbHeader}
            <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 6 }}>
              <Skeleton variant="rectangular" height={200} />
            </Box>
          </Box>
        );
      }
      const lastModified = detail.modifiedBy?.transactionDate ?? detail.createdBy?.transactionDate;
      return (
        <Box data-testid="project-variable-view-page">
          {breadcrumbHeader}
          <Box sx={{ paddingX: 6, paddingBottom: 3 }}>
            <AnalyticalVariableCard
              analyticalVariable={detail.jsonValue}
              conceptGroups={toConceptGroupOptions(conceptGroupsResponse?.data)}
              owner={detail.owner ?? undefined}
              lastModified={lastModified}
            />
          </Box>
        </Box>
      );
    }
    // ``jsonValue`` absent → SQL-first ``av_denovo`` library row,
    // served by the AvContentCard below.
  }

  const detail = detailQuery.data?.data ?? null;
  if (!detail) {
    return (
      <Box data-testid="project-variable-view-page">
        {breadcrumbHeader}
        <Box data-testid="project-variable-view-error" sx={{ paddingX: 6, paddingBottom: 3 }}>
          <Typography variant="subtitle1" color={theme.palette.error.main}>
            {/* OR-6430: name the reference that was looked up so an unresolvable
                link is reportable/debuggable instead of a bare failure. */}
            {analyticalVariableId
              ? `Could not load variable "${analyticalVariableId}".`
              : 'Could not load variable.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="project-variable-view-page"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AvSlugClickProvider onClick={handleAvSlugClick}>
          <AvContentCard
            liveArtifact={liveArtifact}
            detail={detail}
            isStreaming={false}
            headerFallbackName={matchSlug || undefined}
            // Pass the breadcrumb into the card so the breadcrumb + title share
            // one header (tight spacing, matching the other detail pages) rather
            // than stacking a separate breadcrumb header above it.
            breadcrumbs={breadcrumbs}
            data-testid="project-variable-view"
          />
        </AvSlugClickProvider>
      </Box>
    </Box>
  );
};

export default ProjectVariableViewPage;
